import { useTranslation } from "@sagot/i18n";
import { Button, ChoiceChips, DialogContent, Field, Textarea, toast } from "@sagot/ui";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { useAudienceList } from "../shared/use-engage-data";
import type { AddToKnowledgeIn } from "./inbox-api";
import type { MessageView } from "./inbox-model";

const TEXT_MAX = 4000;
const PUBLIC_AUDIENCE = "1";

interface AddToKnowledgeDialogProps {
  candidate: { message: MessageView; question: string };
  agentId: string;
  onSubmit: (body: AddToKnowledgeIn) => Promise<unknown>;
  onDone: () => void;
}

/** Saves the last staff (or AI) answer + the visitor question as a Q&A source. */
export function AddToKnowledgeDialog({ candidate, agentId, onSubmit, onDone }: AddToKnowledgeDialogProps) {
  const { t } = useTranslation();
  const audiences = useAudienceList();
  const errorMessage = useErrorMessage();
  const schema = z.object({
    question: z.string().trim().min(1, t("engage.inbox.knowledge.questionRequired")).max(TEXT_MAX),
    answer: z.string().trim().min(1, t("engage.inbox.knowledge.answerRequired")).max(TEXT_MAX),
    audiences: z.array(z.string()).min(1, t("engage.inbox.knowledge.audienceRequired")),
  });
  type Values = z.infer<typeof schema>;
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { question: candidate.question, answer: candidate.message.content, audiences: [PUBLIC_AUDIENCE] },
  });
  const options = (audiences.data ?? []).map((a) => ({ value: String(a.code), label: a.label }));

  const submit = form.handleSubmit(async (values) => {
    try {
      await onSubmit({
        message_id: candidate.message.id,
        question: values.question,
        answer: values.answer,
        audiences: values.audiences.map(Number),
        agent_ids: [agentId],
      });
      toast.success(t("engage.inbox.knowledge.saved"));
      onDone();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  });

  return (
    <DialogContent
      title={t("engage.inbox.knowledge.title")}
      description={t("engage.inbox.knowledge.description")}
      closeLabel={t("common.close")}
      footer={
        <>
          <Button variant="ghost" onClick={onDone}>
            {t("common.cancel")}
          </Button>
          <Button loading={form.formState.isSubmitting} onClick={() => void submit()}>
            {t("engage.inbox.knowledge.save")}
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)} noValidate>
        <Field label={t("engage.inbox.knowledge.question")} error={form.formState.errors.question?.message}>
          <Textarea rows={2} placeholder={t("engage.inbox.knowledge.questionPlaceholder")} {...form.register("question")} />
        </Field>
        <Field label={t("engage.inbox.knowledge.answer")} error={form.formState.errors.answer?.message}>
          <Textarea rows={5} {...form.register("answer")} />
        </Field>
        <div>
          <div className="label">{t("engage.inbox.knowledge.audiences")}</div>
          <Controller
            control={form.control}
            name="audiences"
            render={({ field }) => (
              <ChoiceChips multiple aria-label={t("engage.inbox.knowledge.audiences")} value={field.value} onValueChange={field.onChange} options={options} />
            )}
          />
          {form.formState.errors.audiences ? <p className="mt-1 text-xs font-semibold text-danger">{form.formState.errors.audiences.message}</p> : null}
        </div>
      </form>
    </DialogContent>
  );
}
